/**
 * PHARMAKON — display-preferences store (theme, density, sidebar, tweaks panel)
 *
 * Novera Pro (refonte D-23, design/ux/01_foundations.md §2.9) is the only
 * identity offered: « Clair » (`novera`, the default), « Sombre »
 * (`novera-dark`, Novera Pro Nuit) and « Automatique » (follows the OS,
 * resolved at start-up and on every `prefers-color-scheme` change).
 * Persisted to localStorage under `pharmakon_theme` (OP-F-06).
 *
 * Slices
 *   theme            — resolved theme written to <html data-theme>
 *   themePref        — user preference: a resolved theme or "auto"
 *   density          — 'comfortable' | 'compact' (<html data-density>);
 *                      density never changes the 16 px root size (§2.5)
 *   sidebarCollapsed — boolean (rail compact at ≥ 1280 px)
 *   navHoverOpen     — boolean, flyouts open on hover (OP-F-24, default on)
 *   mobileNavOpen    — transient drawer flag (never persisted)
 *   showTweaks       — transient: the display-preferences panel visibility
 *
 * Persisted migration (version 0 → 1): light / dark / auto become novera /
 * novera-dark / auto; unknown values fall back to novera; the retired accent
 * picker's `accent` key is dropped (AUD-UXDS-14).
 *
 * Legacy compat: `toggleTheme` and `setTheme` keep their signatures.
 */
import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import {
  THEMES,
  THEME_AUTO,
  DEFAULT_THEME_PREF,
  LEGACY_THEME_PREF_MIGRATION,
  PREFERS_DARK_QUERY,
  DENSITIES,
  DEFAULT_DENSITY,
  THEME_STORAGE_KEY,
  THEME_STORAGE_VERSION,
} from "@/config/ui.config.js";

const RESOLVED_THEMES = new Set(Object.values(THEMES));
const DENSITY_VALUES = new Set(Object.values(DENSITIES));

/** Any persisted/legacy preference → a current preference (theme or auto). */
export const normalizeThemePref = (pref) => {
  if (pref === THEME_AUTO || RESOLVED_THEMES.has(pref)) return pref;
  return typeof pref === "string" &&
    Object.prototype.hasOwnProperty.call(LEGACY_THEME_PREF_MIGRATION, pref)
    ? LEGACY_THEME_PREF_MIGRATION[pref]
    : DEFAULT_THEME_PREF;
};

export const normalizeDensity = (density) =>
  DENSITY_VALUES.has(density) ? density : DEFAULT_DENSITY;

/** Preference + OS signal → the theme written to <html data-theme>. */
export const resolveTheme = (pref, prefersDark) => {
  const p = normalizeThemePref(pref);
  if (p !== THEME_AUTO) return p;
  return prefersDark ? THEMES.SOMBRE : THEMES.CLAIR;
};

/** True for the dark identity (and the retired neon dark, still in CSS). */
export const isDarkTheme = (theme) => theme === THEMES.SOMBRE || theme === "dark";

const mediaQuery = () =>
  typeof window !== "undefined" && typeof window.matchMedia === "function"
    ? window.matchMedia(PREFERS_DARK_QUERY)
    : null;

export const systemPrefersDark = () => Boolean(mediaQuery()?.matches);

const applyThemeToDom = (theme) => {
  if (typeof document === "undefined") return;
  document.documentElement.setAttribute("data-theme", theme);
};
const applyDensityToDom = (density) => {
  if (typeof document === "undefined") return;
  document.documentElement.setAttribute("data-density", density);
};

/** Persisted state of any version → the current persisted shape. */
export const migrateThemeState = (persisted) => {
  const s = persisted && typeof persisted === "object" ? persisted : {};
  return {
    themePref: normalizeThemePref(s.themePref ?? s.theme),
    density: normalizeDensity(s.density),
    sidebarCollapsed: Boolean(s.sidebarCollapsed),
    // OP-F-24 — on unless the user turned it off (D-21).
    navHoverOpen: s.navHoverOpen !== false,
  };
};

export const useThemeStore = create(
  persist(
    (set, get) => ({
      theme: resolveTheme(DEFAULT_THEME_PREF, systemPrefersDark()),
      themePref: DEFAULT_THEME_PREF,
      density: DEFAULT_DENSITY,
      sidebarCollapsed: false,
      navHoverOpen: true,
      showTweaks: false,

      // ── Theme ─────────────────────────────────────────────────────────────
      // Flips Clair ↔ Sombre from the theme on screen; the choice becomes an
      // explicit preference (it leaves "Automatique").
      toggleTheme: () => {
        const next = isDarkTheme(get().theme) ? THEMES.CLAIR : THEMES.SOMBRE;
        applyThemeToDom(next);
        set({ theme: next, themePref: next });
      },
      setTheme: (pref) => {
        const themePref = normalizeThemePref(pref);
        const theme = resolveTheme(themePref, systemPrefersDark());
        applyThemeToDom(theme);
        set({ theme, themePref });
      },
      /** Re-resolves "Automatique" after an OS change (matchMedia listener). */
      syncSystemTheme: () => {
        if (get().themePref !== THEME_AUTO) return;
        const theme = resolveTheme(THEME_AUTO, systemPrefersDark());
        if (theme === get().theme) return;
        applyThemeToDom(theme);
        set({ theme });
      },

      // ── Density ───────────────────────────────────────────────────────────
      setDensity: (value) => {
        const density = normalizeDensity(value);
        applyDensityToDom(density);
        set({ density });
      },

      // ── Sidebar ───────────────────────────────────────────────────────────
      toggleSidebar: () => set((s) => ({ sidebarCollapsed: !s.sidebarCollapsed })),
      setSidebarCollapsed: (sidebarCollapsed) => set({ sidebarCollapsed }),

      // ── Menu flyouts (ux/01 §6.4, OP-F-24) ────────────────────────────────
      setNavHoverOpen: (on) => set({ navHoverOpen: Boolean(on) }),

      // ── Mobile nav drawer ─────────────────────────────────────────────────
      // Transient by design: never persisted, so a reload never lands on a
      // page with the drawer already covering the content. Desktop ignores it
      // entirely (the CSS only reads `data-mobile-nav` inside the media query).
      mobileNavOpen: false,
      toggleMobileNav: () => set((s) => ({ mobileNavOpen: !s.mobileNavOpen })),
      closeMobileNav: () => set({ mobileNavOpen: false }),

      // ── Display-preferences panel ─────────────────────────────────────────
      toggleTweaks: () => set((s) => ({ showTweaks: !s.showTweaks })),
      setShowTweaks: (showTweaks) => set({ showTweaks }),
    }),
    {
      name: THEME_STORAGE_KEY,
      version: THEME_STORAGE_VERSION,
      storage: createJSONStorage(() => localStorage),
      migrate: (persisted) => migrateThemeState(persisted),
      // The resolved theme is never trusted from storage: it is recomputed
      // from the preference and the OS signal at every start-up.
      merge: (persisted, current) => {
        const s = migrateThemeState(persisted ?? current);
        return {
          ...current,
          ...s,
          theme: resolveTheme(s.themePref, systemPrefersDark()),
        };
      },
      // Runs synchronously at module load (localStorage), before the first
      // render: <html> carries the right theme and density from the start.
      onRehydrateStorage: () => (state) => {
        if (!state) return;
        applyThemeToDom(state.theme);
        applyDensityToDom(state.density);
      },
      // Persist user preferences only; transient flags reset on load.
      partialize: (s) => ({
        themePref: s.themePref,
        density: s.density,
        sidebarCollapsed: s.sidebarCollapsed,
        navHoverOpen: s.navHoverOpen,
      }),
    },
  ),
);

// "Automatique" follows the OS live (AUD-UXDS-20): one listener per tab.
(() => {
  const mq = mediaQuery();
  if (!mq) return;
  const onChange = () => useThemeStore.getState().syncSystemTheme();
  if (typeof mq.addEventListener === "function") mq.addEventListener("change", onChange);
  else if (typeof mq.addListener === "function") mq.addListener(onChange);
})();
